import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { Dispatch, SetStateAction } from 'react';
import { toast } from '@/ui/toast';
import { extractApiErrorMessage } from '@/components/action-buttons/group-merge.helpers';
import type { GroupMergePayload } from '@/components/action-buttons/use-group-merge-modal-orchestration';

type MergeGroupsResponse = {
    data: {
        targetGroupId: string;
    };
};

type ExecuteGroupMergeActionArgs = GroupMergePayload & {
    setIsGroupsMergeLoading: Dispatch<SetStateAction<boolean>>;
    setPickedGroupIds?: (groupIds: string[]) => void;
    mergeGroups: (payload: GroupMergePayload) => Promise<MergeGroupsResponse>;
    refetchReviewsAndProducts: () => Promise<void>;
    syncSelectionAfterMerge?: () => Promise<void>;
};

const groupMergeLogger = createClientLogger({
    scope: 'group-merge',
});

export const executeGroupMergeAction = async ({
    groupIds,
    name,
    setIsGroupsMergeLoading,
    setPickedGroupIds,
    mergeGroups,
    refetchReviewsAndProducts,
    syncSelectionAfterMerge,
}: ExecuteGroupMergeActionArgs): Promise<boolean> => {
    if (groupIds.length < 2) {
        toast.warning('Выберите минимум 2 группы');
        return false;
    }

    if (!name) {
        return false;
    }

    if (!setPickedGroupIds) {
        return false;
    }

    setIsGroupsMergeLoading(true);

    try {
        await mergeGroups({ groupIds, name });
        setPickedGroupIds([]);

        if (syncSelectionAfterMerge) {
            await syncSelectionAfterMerge();
        }

        await refetchReviewsAndProducts();
        toast.success('Группы объединены');
        return true;
    } catch (error) {
        logClientError({
            logger: groupMergeLogger,
            message: '[client.group-management.merge-selected] Failed to merge selected groups.',
            error,
            context: {
                groupIds: groupIds.length,
            },
        });
        toast.error(extractApiErrorMessage(error) || 'Не удалось объединить группы');
        return false;
    } finally {
        setIsGroupsMergeLoading(false);
    }
};
